import React, { useState } from 'react';
import { useExportManager, EXPORT_TYPES, PRIVACY_LEVELS } from '../utils/exportManager';

/**
 * Export and Share Controls Component
 * Provides UI for exporting and sharing content across pages
 */
const ExportShareControls = ({ 
  data, 
  contentType, 
  title = 'Export',
  showBackupControls = true,
  className = ''
}) => {
  const { exportManager, sharingManager, isExportEnabled, isSharingEnabled } = useExportManager();
  
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [showBackupMenu, setShowBackupMenu] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // Don't render if both export and sharing are disabled
  if (!isExportEnabled && !isSharingEnabled) {
    return null;
  }

  /**
   * Handle export based on content type
   */
  const handleExport = async (exportType) => {
    setIsExporting(true);
    setShowExportMenu(false);
    
    try {
      let success = false;
      
      switch (contentType) {
        case 'gedcom':
          success = await exportManager.exportGedcom(data);
          break;
        case 'calendar':
          success = await exportManager.exportCalendar(data, { title });
          break;
        case 'family_stats':
        case 'family_tree':
          if (exportType === EXPORT_TYPES.PDF) {
            success = await exportManager.exportPDF(data, contentType, { title });
          } else if (exportType === EXPORT_TYPES.GEDCOM) {
            success = await exportManager.exportGedcom(data);
          }
          break;
        default:
          console.warn('Unknown content type:', contentType);
      }
      
      if (!success) {
        alert('Export failed. Please try again.');
      }
    } catch (error) {
      console.error('Export error:', error);
      alert(error.message || 'Export failed. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  /**
   * Handle sharing
   */
  const handleShare = async (privacyLevel) => {
    setShowShareMenu(false);
    
    try {
      const shareLink = sharingManager.generateShareableLink(data, privacyLevel);
      const copied = await sharingManager.copyShareLink(shareLink);
      
      if (!copied) {
        // Fallback: show the link in a prompt
        prompt('Share this link:', shareLink);
      }
    } catch (error) {
      console.error('Sharing error:', error);
      alert(error.message || 'Sharing failed. Please try again.');
    }
  };

  /**
   * Handle backup creation
   */
  const handleCreateBackup = () => {
    setShowBackupMenu(false);
    const success = exportManager.createBackup(data, contentType);
    
    if (!success) {
      alert('Backup failed. Please try again.');
    }
  };

  /**
   * Handle backup restore
   */
  const handleRestoreBackup = () => {
    setShowBackupMenu(false);
    const backups = exportManager.getAvailableBackups();
    
    if (backups.length === 0) {
      alert('No backups available.');
      return;
    }

    // Show backup selection dialog
    const backupList = backups.map((backup, index) => 
      `${index + 1}. ${backup.type} - ${new Date(backup.timestamp).toLocaleString()}`
    ).join('\n');
    
    const selection = prompt(`Select backup to restore:\n\n${backupList}\n\nEnter number:`);
    
    if (selection) {
      const index = parseInt(selection) - 1;
      if (index >= 0 && index < backups.length) {
        const restoredData = exportManager.restoreFromBackup(backups[index].key);
        if (restoredData) {
          alert('Backup restored successfully! Please refresh the page.');
        } else {
          alert('Failed to restore backup.');
        }
      }
    }
  };

  /**
   * Handle backup cleanup
   */
  const handleCleanBackups = () => {
    setShowBackupMenu(false);
    const deleted = exportManager.cleanOldBackups();
    alert(`Cleaned ${deleted} old backup(s).`);
  };

  return (
    <div className={`export-share-controls ${className}`}>
      <style>{`
        .export-share-controls {
          display: flex;
          gap: 10px;
          align-items: center;
          position: relative;
        }
        
        .export-share-btn {
          padding: 8px 16px;
          background: #2196F3;
          color: white;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-size: 14px;
          display: flex;
          align-items: center;
          gap: 6px;
          transition: background 0.2s;
        }
        
        .export-share-btn:hover {
          background: #1976D2;
        }
        
        .export-share-btn:disabled {
          background: #ccc;
          cursor: not-allowed;
        }
        
        .export-share-btn.share-btn {
          background: #FF9800;
        }
        
        .export-share-btn.share-btn:hover {
          background: #F57C00;
        }
        
        .export-share-btn.backup-btn {
          background: #4CAF50;
        }
        
        .export-share-btn.backup-btn:hover {
          background: #388E3C;
        }
        
        .dropdown-menu {
          position: absolute;
          top: 100%;
          left: 0;
          margin-top: 5px;
          background: white;
          border: 1px solid #ddd;
          border-radius: 4px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.15);
          z-index: 1000;
          min-width: 200px;
        }
        
        .dropdown-item {
          padding: 10px 15px;
          cursor: pointer;
          border-bottom: 1px solid #f0f0f0;
          transition: background 0.2s;
        }
        
        .dropdown-item:last-child {
          border-bottom: none;
        }
        
        .dropdown-item:hover {
          background: #f5f5f5;
        }
        
        .dropdown-item i {
          margin-right: 8px;
          width: 16px;
        }
        
        @media (max-width: 768px) {
          .export-share-controls {
            flex-wrap: wrap;
          }
          
          .export-share-btn {
            font-size: 12px;
            padding: 6px 12px;
          }
        }
      `}</style>

      {/* Export Button */}
      {isExportEnabled && (
        <div style={{ position: 'relative' }}>
          <button
            className="export-share-btn"
            onClick={() => setShowExportMenu(!showExportMenu)}
            disabled={isExporting}
          >
            <i className="fas fa-download"></i>
            {isExporting ? 'Exporting...' : 'Export'}
          </button>
          
          {showExportMenu && (
            <div className="dropdown-menu">
              {contentType === 'gedcom' && (
                <div className="dropdown-item" onClick={() => handleExport(EXPORT_TYPES.GEDCOM)}>
                  <i className="fas fa-file-code"></i>
                  Export as GEDCOM
                </div>
              )}
              {contentType === 'calendar' && (
                <div className="dropdown-item" onClick={() => handleExport(EXPORT_TYPES.CALENDAR)}>
                  <i className="fas fa-calendar"></i>
                  Export as iCal
                </div>
              )}
              {(contentType === 'family_stats' || contentType === 'family_tree') && (
                <>
                  <div className="dropdown-item" onClick={() => handleExport(EXPORT_TYPES.PDF)}>
                    <i className="fas fa-file-pdf"></i>
                    Export as PDF Report
                  </div>
                  <div className="dropdown-item" onClick={() => handleExport(EXPORT_TYPES.GEDCOM)}>
                    <i className="fas fa-file-code"></i>
                    Export as GEDCOM
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* Share Button */}
      {isSharingEnabled && (
        <div style={{ position: 'relative' }}>
          <button
            className="export-share-btn share-btn"
            onClick={() => setShowShareMenu(!showShareMenu)}
          >
            <i className="fas fa-share-alt"></i>
            Share
          </button>
          
          {showShareMenu && (
            <div className="dropdown-menu">
              <div className="dropdown-item" onClick={() => handleShare(PRIVACY_LEVELS.FAMILY_ONLY)}>
                <i className="fas fa-users"></i>
                Share with Family
              </div>
              <div className="dropdown-item" onClick={() => handleShare(PRIVACY_LEVELS.PUBLIC)}>
                <i className="fas fa-globe"></i>
                Share Publicly
              </div>
              <div className="dropdown-item" onClick={() => handleShare(PRIVACY_LEVELS.PRIVATE)}>
                <i className="fas fa-lock"></i>
                Private Link
              </div>
            </div>
          )}
        </div>
      )}

      {/* Backup Controls */}
      {showBackupControls && isExportEnabled && (
        <div style={{ position: 'relative' }}>
          <button
            className="export-share-btn backup-btn"
            onClick={() => setShowBackupMenu(!showBackupMenu)}
          >
            <i className="fas fa-save"></i>
            Backup
          </button>
          
          {showBackupMenu && (
            <div className="dropdown-menu">
              <div className="dropdown-item" onClick={handleCreateBackup}>
                <i className="fas fa-plus"></i>
                Create Backup
              </div>
              <div className="dropdown-item" onClick={handleRestoreBackup}>
                <i className="fas fa-undo"></i>
                Restore Backup
              </div>
              <div className="dropdown-item" onClick={handleCleanBackups}>
                <i className="fas fa-trash"></i>
                Clean Old Backups
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ExportShareControls;
